<template>
  <div>
    <search-form @search-update="searchUpdate"></search-form>
    <p class="is-small search__count" v-if="searchTerm">
      Showing {{ totalCount }} results for {{ searchTerm }}
    </p>
    <div class="tablist" role="tablist" aria-label="search results">
      <div class="tablist-wrap">
        <button
          v-for="(values, key) in filteredResults"
          :key="key"
          class="tab"
          role="tab"
          :aria-selected="activeTab === key"
          @click="switchTab(key)"
        >
          {{ tabNames[key] }}
        </button>
      </div>
    </div>
    <div
      v-for="(values, key) in filteredResults"
      class="results"
      role="tabpanel"
      tabindex="0"
      :id="`tab-${key}`"
      :key="key"
      :hidden="activeTab !== key"
    >
      <template v-if="key === 'pages'">
        <template v-for="page in values">
          <insight-result
            v-if="page.meta.type === 'insights.InsightPage'"
            :key="page.id"
            :page-id="page.id"
          ></insight-result>
          <search-result
            v-else
            :key="page.id"
            :page-id="page.id"
            page-type="Related Page"
            :title="page.title"
          ></search-result>
        </template>
      </template>
      <template v-if="key === 'drugFacts'">
        <search-result
          v-for="page in values"
          :key="page.id"
          :title="page.title"
          page-type="Drug Fact Page"
          :url="page.url"
          :breadcrumbs="page.breadcrumbs"
          :description="page.short_description"
        ></search-result>
      </template>
      <template v-if="key === 'supportServices'">
        <service
          v-for="service in values"
          :key="service.id"
          :name="service.name"
          :location="service.location"
          :description="service.description"
          :contact="service.contact"
          :url="service.url"
          :tags="service.tags"
        ></service>
      </template>
      <div v-if="key === 'downloads'" class="downloads module">
        <download
          v-for="download in values"
          :key="download.id"
          :title="download.document_title"
          :url="download.document_url"
          :type="download.document_type"
          :size="download.document_size"
          :format="download.document_format"
        ></download>
      </div>
      <div class="module" v-if="results[key].total > results[key].results.length">
        <button class="button" @click="loadMore(key)">Load more {{ tabNames[key] }}</button>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SearchForm from './SearchForm.vue';
import SearchResult from './SearchResult.vue';
import InsightResult from './InsightResult.vue';
import Service from '@/components/services/Service.vue';
import Download from '@/components/standard/Download.vue';

export default {
  name: 'search',
  components: {
    SearchForm,
    SearchResult,
    InsightResult,
    Service,
    Download,
  },
  data() {
    return {
      activeTab: 'drugFacts',
      results: {
        drugFacts: {
          total: 0,
          offset: 0,
          results: [],
        },
        pages: {
          total: 0,
          offset: 0,
          results: [],
        },
        downloads: {
          total: 0,
          offset: 0,
          results: [],
        },
        supportServices: {
          total: 0,
          offset: 0,
          results: [],
        },
      },
      searchTerm: '',
      tabNames: {
        drugFacts: 'Drug Facts',
        pages: 'Pages',
        insights: 'Insights',
        downloads: 'Downloads',
        supportServices: 'Support Services',
      },
    };
  },
  mounted() {
    const urlParams = new URLSearchParams(window.location.search);
    const searchTerm = urlParams.get('search');
    if (searchTerm) {
      this.searchTerm = searchTerm;
      this.updateSearch();
    }
  },
  computed: {
    filteredResults() {
      return Object.keys(this.results).reduce((filtered, key) => {
        if (this.results[key].results.length > 0) {
          filtered[key] = this.results[key].results; // eslint-disable-line no-param-reassign
        }
        return filtered;
      }, {});
    },
    totalCount() {
      return Object.keys(this.results).reduce((total, key) => total + this.results[key].total, 0);
    },
  },
  methods: {
    /**
     * Switch which tab is open
     */
    switchTab(tabName) {
      this.activeTab = tabName;
    },
    /**
     * update search term when form submitted.
     */
    searchUpdate(payload) {
      this.searchTerm = payload;
      this.updateSearch();
    },
    /**
     * Run search query
     */
    updateSearch() {
      this.searchDrugFactPages();
      this.searchStandardAndInsightPages();
      this.supportServices();
      this.downloads();
    },
    /**
     * Load more results
     * @param {String} Key of resource to load more of
     */
    loadMore(key) {
      switch (key) {
        case 'pages':
          this.searchStandardAndInsightPages();
          break;
        case 'downloads':
          this.downloads();
          break;
        case 'supportServices':
          this.supportServices();
          break;
        case 'drugFacts':
          this.searchDrugFactPages();
          break;
        default:
          break;
      }
    },
    /**
     * search standard pages.
     */
    searchStandardAndInsightPages() {
      let url = `${process.env.VUE_APP_API_URL}pages/?type=insights.InsightPage,standard.StandardPage`;
      url += `&search=${this.searchTerm}`;
      url += `&limit=5&offset=${this.results.pages.offset}`;
      axios.get(url)
        .then((response) => {
          this.results.pages.results = this.results.pages.results.concat(response.data.items);
          this.results.pages.total = response.data.meta.total_count;
          this.results.pages.offset += 5;
        });
    },
    /**
     * search drug fact pages.
     */
    searchDrugFactPages() {
      let url = `${process.env.VUE_APP_API_URL}pages/?type=drug_facts.DrugFactPage&fields=*`;
      url += `&search=${this.searchTerm}`;
      url += `&limit=5&offset=${this.results.drugFacts.offset}`;
      axios.get(url)
        .then((response) => {
          this.results.drugFacts.results = this.results.drugFacts.results
            .concat(response.data.items);
          this.results.drugFacts.total = response.data.meta.total_count;
          this.results.drugFacts.offset += 5;
        });
    },
    /**
     * search support services
     */
    supportServices() {
      let url = `${process.env.VUE_APP_API_URL}services/`;
      url += `?search=${this.searchTerm}`;
      url += `&limit=5&offset=${this.results.supportServices.offset}`;
      axios.get(url)
        .then((response) => {
          this.results.supportServices.results = this.results.supportServices.results
            .concat(response.data.items);
          this.results.supportServices.total = response.data.meta.total_count;
          this.results.supportServices.offset += 5;
        });
    },
    /**
     * get downloads
     */
    downloads() {
      let url = `${process.env.VUE_APP_API_URL}downloads/`;
      url += `?search=${this.searchTerm}`;
      url += `&limit=10&offset=${this.results.downloads.offset}`;
      axios.get(url)
        .then((response) => {
          this.results.downloads.results = this.results.downloads.results
            .concat(response.data.items);
          this.results.downloads.total = response.data.meta.total_count;
          this.results.downloads.offset += 10;
        });
    },
  },
};
</script>
